import React from "react";
const { Children: Children_G3D, cloneElement: cloneElement_G3D, isValidElement: isValidElement_G3D, useEffect: useEffect_G3D, useRef: useRef_G3D } = React;

function Glyph3D({ children, size = 88 }) {
  const wrapRef = useRef_G3D(null);
  const stackRef = useRef_G3D(null);
  const layerRefs = useRef_G3D([]);

  useEffect_G3D(() => {
    const wrap = wrapRef.current;
    const stack = stackRef.current;
    if (!wrap || !stack) return;
    const prefersReducedMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const card = wrap.closest(".card") || wrap;

    let raf = 0;
    const state = { tx: 0, ty: 0, cx: 0, cy: 0, inside: false, start: performance.now() };

    function onMove(e) {
      const r = card.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right &&
        e.clientY >= r.top && e.clientY <= r.bottom;
      state.inside = inside;
      if (!inside) { state.tx = 0; state.ty = 0; return; }
      state.tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      state.ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
    }
    function onLeave() { state.inside = false; state.tx = 0; state.ty = 0; }
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    function frame(now) {
      raf = requestAnimationFrame(frame);
      const t = (now - state.start) / 1000;
      state.cx += (state.tx - state.cx) * 0.08;
      state.cy += (state.ty - state.cy) * 0.08;
      const idleRx = Math.sin(t * 0.5) * 4;
      const idleRy = Math.cos(t * 0.4) * 6;
      const hoverBoost = state.inside ? 1 : 0.6;
      const rx = (-state.cy * 18 + idleRx) * hoverBoost;
      const ry = (state.cx * 22 + idleRy) * hoverBoost;
      stack.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
      const layers = layerRefs.current;
      for (let i = 0; i < layers.length; i++) {
        const el = layers[i];
        if (!el) continue;
        const baseZ = parseFloat(el.dataset.z || "0");
        const drift = Math.sin(t * 0.9 + i * 0.7) * 3;
        el.style.transform = `translateZ(${(baseZ + drift).toFixed(2)}px)`;
      }
    }
    if (!prefersReducedMotion) raf = requestAnimationFrame(frame);
    else {
      stack.style.transform = "rotateX(8deg) rotateY(-10deg)";
      const layers = layerRefs.current;
      for (let i = 0; i < layers.length; i++) {
        const el = layers[i];
        if (!el) continue;
        el.style.transform = `translateZ(${el.dataset.z || 0}px)`;
      }
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  layerRefs.current = [];
  const childArr = Children_G3D.toArray(children).map((child, i) => {
    if (!isValidElement_G3D(child)) return child;
    return cloneElement_G3D(child, {
      _registerRef: (node) => { layerRefs.current[i] = node; },
    });
  });

  return (
    <div
      ref={wrapRef}
      style={{ width: size, height: size, perspective: "320px", perspectiveOrigin: "50% 50%" }}
    >
      <div
        ref={stackRef}
        style={{
          position: "relative", width: "100%", height: "100%",
          transformStyle: "preserve-3d", willChange: "transform",
          transition: "transform 80ms linear",
        }}
      >
        {childArr}
      </div>
    </div>
  );
}

function Layer3D({ z = 0, children, _registerRef }) {
  return (
    <div
      ref={_registerRef}
      data-z={z}
      style={{ position: "absolute", inset: 0, transformStyle: "preserve-3d", willChange: "transform" }}
    >
      {children}
    </div>
  );
}

export { Glyph3D, Layer3D };
